if (!customElements.get('presse-timuntu-slider')) {
  class PresseTimuntuSlider extends HTMLElement {
    constructor() {
      super();
      this.viewport = this.querySelector('.presse-timuntu__slider-viewport');
      this.track = this.querySelector('[data-slider-track]');
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      this.dotsWrap = this.querySelector('[data-slider-dots]');
      this.slides = Array.from(this.querySelectorAll('.presse-timuntu__slide'));
      this.currentIndex = 0;
      this.timer = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.isHovering = false;
      this.touchStartX = 0;
      this.touchCurrentX = 0;
    }

    connectedCallback() {
      if (!this.track || !this.viewport || this.slides.length === 0) return;

      this.autoplayEnabled = this.dataset.autoplayEnabled === 'true' && !this.reducedMotion;
      this.loop = this.dataset.loop === 'true';
      this.showDots = this.dataset.showDots === 'true';
      this.intervalMs = Number(this.dataset.autoplayInterval || 3500);

      this.buildDots();
      this.bindEvents();
      this.update();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }

    getPerView() {
      const desktop = Number(this.dataset.perViewDesktop || 4);
      const mobile = Number(this.dataset.perViewMobile || 2);
      return window.innerWidth >= 990 ? desktop : mobile;
    }

    getMaxIndex() {
      return Math.max(0, this.slides.length - this.getPerView());
    }

    goTo(index) {
      const maxIndex = this.getMaxIndex();
      if (this.loop) {
        if (index > maxIndex) index = 0;
        if (index < 0) index = maxIndex;
      } else {
        index = Math.min(maxIndex, Math.max(0, index));
      }
      this.currentIndex = index;
      this.update();
    }

    update() {
      const firstSlide = this.slides[0];
      if (!firstSlide) return;
      const slideWidth = firstSlide.getBoundingClientRect().width;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap || 0);
      const offset = (slideWidth + gap) * this.currentIndex;
      this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;

      if (this.prevBtn && this.nextBtn && !this.loop) {
        this.prevBtn.disabled = this.currentIndex <= 0;
        this.nextBtn.disabled = this.currentIndex >= this.getMaxIndex();
      }
      this.updateDots();
    }

    buildDots() {
      if (!this.showDots || !this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      const count = this.getMaxIndex() + 1;
      if (count <= 1) return;

      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'presse-timuntu__dot';
        dot.setAttribute('aria-label', `Aller au slide ${i + 1}`);
        dot.addEventListener('click', () => {
          this.goTo(i);
          this.restartAutoplay();
        });
        this.dotsWrap.appendChild(dot);
      }
      this.updateDots();
    }

    updateDots() {
      if (!this.dotsWrap) return;
      const dots = this.dotsWrap.querySelectorAll('.presse-timuntu__dot');
      dots.forEach((dot, idx) => {
        dot.classList.toggle('is-active', idx === this.currentIndex);
      });
    }

    bindEvents() {
      this.prevBtn?.addEventListener('click', () => {
        this.goTo(this.currentIndex - 1);
        this.restartAutoplay();
      });
      this.nextBtn?.addEventListener('click', () => {
        this.goTo(this.currentIndex + 1);
        this.restartAutoplay();
      });

      this.addEventListener('mouseenter', () => {
        this.isHovering = true;
        this.stopAutoplay();
      });
      this.addEventListener('mouseleave', () => {
        this.isHovering = false;
        this.startAutoplay();
      });
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => {
        if (!this.contains(document.activeElement)) this.startAutoplay();
      });

      this.viewport.addEventListener('touchstart', (event) => {
        this.touchStartX = event.touches[0].clientX;
        this.touchCurrentX = this.touchStartX;
        this.stopAutoplay();
      }, { passive: true });

      this.viewport.addEventListener('touchmove', (event) => {
        this.touchCurrentX = event.touches[0].clientX;
      }, { passive: true });

      this.viewport.addEventListener('touchend', () => {
        const delta = this.touchCurrentX - this.touchStartX;
        if (Math.abs(delta) > 40) {
          if (delta < 0) this.goTo(this.currentIndex + 1);
          if (delta > 0) this.goTo(this.currentIndex - 1);
        }
        this.startAutoplay();
      });

      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.currentIndex + 1);
          this.restartAutoplay();
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.currentIndex - 1);
          this.restartAutoplay();
        }
      });

      window.addEventListener('resize', () => {
        this.currentIndex = Math.min(this.currentIndex, this.getMaxIndex());
        this.buildDots();
        this.update();
      });
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.isHovering || this.getMaxIndex() <= 0) return;
      this.stopAutoplay();
      this.timer = window.setInterval(() => {
        this.goTo(this.currentIndex + 1);
      }, this.intervalMs);
    }

    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('presse-timuntu-slider', PresseTimuntuSlider);
}
